import type { ConfirmedExpReadout, ExpExactCandidate, ExpReadoutReason, ExpReadoutSnapshot, ExpRecoveryMetric, GameEvent, GameStateSnapshot, PartialExpStatistics, ValidatedObservation } from '@/domain/types'
import { SessionExpCounter, type UncommittedExpReview } from './session-exp-counter'
import { SessionStartExpConsensus } from './session-start-baseline'
import type { ExpDisplayUpdate } from '@/domain/types'

const HOUR_MS = 3_600_000
const RECOVERY_WARMUP_MS = 10_000

/** Recovery rates and confirmed segments never change the main counter's full-session proof. */
export class SessionExpReadout {
  private holdMs = 15_000
  private lastConfirmed: ConfirmedExpReadout | null = null
  private sessionConfirmed: ConfirmedExpReadout | null = null
  private recovering = false
  private boundary = -Infinity
  private gapReason: ExpReadoutReason | null = null
  private recovery: SessionExpCounter
  constructor(onRecoveryUpdate?: (display: ExpDisplayUpdate, invalidated: boolean, uncommitted?: UncommittedExpReview) => void,
    private readonly onRecoveryBoundary?: (at: number) => void) {
    this.recovery = new SessionExpCounter(onRecoveryUpdate, true)
  }
  private consensus: SessionStartExpConsensus | null = null
  private startedAt: number | null = null
  private baselineActiveAt = 0
  private lastBaselineSampleAt: number | null = null
  private since: number | null = null
  private previousStatus: ExpReadoutSnapshot['status'] = 'ready'
  private sessionStartedAt: number | null = null
  private sessionBaselineActiveAt = 0
  private sessionDuration = 0
  private partial: PartialExpStatistics | null = null
  private confirmedRecovery: { startedAt: number; endedAt: number; gained: number; durationMs: number } | null = null

  setSamplingInterval(intervalMs: number): void {
    if (Number.isFinite(intervalMs) && intervalMs > 0) this.holdMs = Math.max(15_000, intervalMs * 3)
  }

  reset(): void {
    this.lastConfirmed = this.sessionConfirmed = null
    this.recovering = false
    this.boundary = -Infinity
    this.gapReason = null
    this.resetRecovery()
    this.since = null
    this.previousStatus = 'ready'
    this.sessionStartedAt = null
    this.sessionBaselineActiveAt = 0
    this.sessionDuration = 0
    this.partial = null
    this.confirmedRecovery = null
  }

  breakContinuity(at: number): void {
    this.closeConfirmedRecovery()
    this.onRecoveryBoundary?.(at)
    this.boundary = at
    this.gapReason = 'continuity-break'
    this.resetRecovery()
  }

  private resetRecovery(): void {
    this.recovery.reset()
    this.consensus = null
    this.startedAt = this.lastBaselineSampleAt = null
    this.baselineActiveAt = 0
    this.lastConfirmed = null
  }

  ingest(main: SessionExpCounter, state: GameStateSnapshot, observation: ValidatedObservation | undefined,
    events: readonly GameEvent[], minimumConfidence: number, activeAt: (sourceAt: number) => number): void {
    const now = state.capturedAt
    if (!main.isIncomplete()) {
      if (this.recovering) {
        this.recovering = false
        this.resetRecovery()
      }
      this.gapReason = null
      this.partial = null
      this.confirmedRecovery = null
      const previous = this.lastConfirmed
      this.remember(main, activeAt)
      if (this.lastConfirmed && this.lastConfirmed !== previous) {
        this.sessionConfirmed = this.lastConfirmed
        if (this.sessionStartedAt === null) this.sessionBaselineActiveAt = activeAt(this.sessionConfirmed.sourceAt)
        this.sessionStartedAt ??= this.sessionConfirmed.sourceAt
        this.sessionDuration = Math.max(0, activeAt(this.sessionConfirmed.sourceAt) - this.sessionBaselineActiveAt)
      }
      return
    }
    this.partialStatistics(main)
    if (!this.recovering) {
      this.recovering = true
      this.boundary = Math.max(this.boundary, main.incompleteSince() ?? now)
      this.gapReason ??= 'confirmation-timeout'
      this.resetRecovery()
    }
    this.recovery.expireJourney(now)
    const lastAt = this.recovery.latestSourceAt() ?? this.lastBaselineSampleAt
    if (this.recovery.isIncomplete() || lastAt !== null && now - lastAt > this.holdMs) {
      this.breakContinuity(now)
    }
    if (this.startedAt === null) {
      const percent = observation?.expEstimatedAdmission?.status === 'accepted' ? observation.expEstimatedEvidence : undefined
      if (percent && percent.value.absolute !== null && percent.level !== null && percent.sourceAt > this.boundary
        && percent.levelSourceAt != null && percent.levelSourceAt > this.boundary && now - percent.sourceAt <= this.holdMs) {
        this.startedAt = percent.sourceAt
        this.baselineActiveAt = activeAt(percent.sourceAt)
        this.recovery.reset({ ...state, capturedAt: percent.sourceAt, level: percent.level, exp: percent.value,
          observedAt: { ...state.observedAt!, exp: percent.sourceAt, level: percent.levelSourceAt } }, percent.sourceAt)
        this.confirmRecovery(activeAt)
        return
      }
      // Two newly accepted endpoints with a refreshed, matching level establish a new origin.
      const candidate = observation && this.baselineCandidate(observation, minimumConfidence)
      if (!candidate) return
      this.consensus ??= new SessionStartExpConsensus(this.boundary, minimumConfidence)
      const result = this.consensus.observe(candidate)
      if (result.status === 'ignored') return
      this.lastBaselineSampleAt = candidate.sourceAt
      if (!result.confirmed) return
      const confirmed = result.confirmed
      this.startedAt = confirmed.sourceAt
      this.baselineActiveAt = activeAt(confirmed.sourceAt)
      this.recovery.reset({ ...state, capturedAt: confirmed.sourceAt, level: confirmed.level,
        exp: confirmed.value, observedAt: { ...state.observedAt!, exp: confirmed.sourceAt, level: confirmed.levelSourceAt },
        confidence: { ...state.confidence, exp: confirmed.confidence } }, confirmed.sourceAt)
      this.confirmRecovery(activeAt)
      return
    }
    if (observation) this.recovery.ingestObservation(observation, minimumConfidence)
    this.recovery.confirmLevelUp(events)
    this.confirmRecovery(activeAt)
    this.remember(this.recovery, at => activeAt(at) - this.baselineActiveAt, RECOVERY_WARMUP_MS)
  }

  private confirmRecovery(activeAt: (at: number) => number): void {
    const display = this.recovery.snapshot()
    if (display.status !== 'ready' || display.netGain === null || display.sourceAt === null || this.startedAt === null) return
    this.confirmedRecovery = { startedAt: this.startedAt, endedAt: display.sourceAt, gained: display.netGain,
      durationMs: Math.max(0, activeAt(display.sourceAt) - this.baselineActiveAt) }
    if (this.partial?.gaps.at(-1)?.endedAt === null) this.partial = { ...this.partial,
      gaps: this.partial.gaps.map((gap, i) => i === this.partial!.gaps.length - 1 ? { ...gap, endedAt: this.startedAt } : gap) }
  }

  private closeConfirmedRecovery(): void {
    if (!this.partial) return
    const confirmed = this.confirmedRecovery
    const segments = confirmed ? [...this.partial.segments, { startedAt: confirmed.startedAt, endedAt: confirmed.endedAt, gained: confirmed.gained }] : this.partial.segments
    const last = this.partial.gaps.at(-1)
    this.partial = { ...this.partial, segments,
      gained: this.partial.gained + (confirmed?.gained ?? 0), durationMs: this.partial.durationMs + (confirmed?.durationMs ?? 0),
      gaps: last?.endedAt === null ? this.partial.gaps : [...this.partial.gaps,
        { startedAt: confirmed?.endedAt ?? last?.endedAt ?? this.boundary, endedAt: null, reason: 'interrupted' }] }
    this.confirmedRecovery = null
  }

  finish(main: SessionExpCounter, at: number): void {
    this.partialStatistics(main)
    if (this.confirmedRecovery && this.confirmedRecovery.endedAt < at) this.closeConfirmedRecovery()
  }

  journeyDisplay(main: SessionExpCounter): ExpDisplayUpdate {
    if (!main.isIncomplete() || this.startedAt === null || !this.partial) return main.snapshot()
    const display = this.recovery.snapshot()
    const offset = this.partial.gained
    const change = display.journeyTransition
    return { ...display, netGain: display.netGain === null ? null : offset + display.netGain,
      ...(display.pendingNetGain != null ? { pendingNetGain: offset + display.pendingNetGain } : {}),
      continuity: display.sourceAt === this.startedAt ? 'discontinuous' : display.continuity,
      ...(change?.netOffset != null ? { journeyTransition: { ...change, netOffset: offset + change.netOffset } } : {}) }
  }

  /** One endpoint per uninterrupted segment; long recordings never retain every frame. */
  partialStatistics(main: SessionExpCounter): PartialExpStatistics | undefined {
    if (!main.isIncomplete()) return undefined
    if (!this.partial) {
      const confirmed = this.sessionConfirmed
      this.partial = { basis: 'confirmed-segments', waitingForLevel: false,
        gained: confirmed?.netGain ?? 0, durationMs: this.sessionDuration,
        segments: confirmed ? [{ startedAt: this.sessionStartedAt ?? confirmed.sourceAt, endedAt: confirmed.sourceAt, gained: confirmed.netGain }] : [],
        gaps: [{ startedAt: confirmed?.sourceAt ?? main.incompleteSince() ?? 0, endedAt: null, reason: 'interrupted' }] }
    }
    const current = this.confirmedRecovery
    return { ...this.partial, gained: this.partial.gained + (current?.gained ?? 0),
      durationMs: this.partial.durationMs + (current?.durationMs ?? 0),
      segments: current ? [...this.partial.segments, { startedAt: current.startedAt, endedAt: current.endedAt, gained: current.gained }] : this.partial.segments }
  }

  private baselineCandidate(observation: ValidatedObservation, minimumConfidence: number): ExpExactCandidate | null {
    const level = observation.level
    if (!observation.accepted.level || level.value === null || !Number.isFinite(level.confidence) || level.confidence < minimumConfidence
      || !Number.isFinite(level.capturedAt)
      || level.capturedAt <= this.boundary || level.capturedAt > observation.capturedAt
      || observation.capturedAt - level.capturedAt > this.holdMs) return null
    const evidence = observation.expExactEvidenceAccepted ? observation.expExactEvidence : undefined
    // accepted.exp refers to the published reading, not an independently pending candidate.
    const source = evidence ?? (observation.expCandidateAdmission?.status === 'accepted' ? observation.expExactCandidate : undefined)
    const value = source?.value ?? (observation.accepted.exp ? observation.exp.value : null)
    const sourceAt = source?.sourceAt ?? observation.exp.capturedAt
    if (!value || value.estimated || value.absolute == null || value.ratio == null
      || !Number.isFinite(sourceAt) || sourceAt <= this.boundary || sourceAt > observation.capturedAt || observation.capturedAt - sourceAt > this.holdMs
      || source && source.level !== level.value) return null
    return { value: { ...value, absolute: value.absolute, ratio: value.ratio }, level: level.value,
      levelSourceAt: level.capturedAt, sourceAt, deliveredAt: observation.capturedAt,
      confidence: source?.confidence ?? observation.exp.confidence,
      percentagePrecision: source?.percentagePrecision ?? value.percentagePrecision ?? 2,
      barConsistency: 'unavailable', recognitionPath: source && 'recognitionPath' in source ? source.recognitionPath : 'legacy' }
  }

  private remember(counter: SessionExpCounter, activeAt: (at: number) => number, minimumDuration = 0): void {
    const display = counter.snapshot()
    if (display.status !== 'ready' || display.netGain === null || !display.point || display.sourceAt === null) return
    const duration = activeAt(display.sourceAt)
    if (duration < minimumDuration) return
    const value = duration > 0 ? display.netGain / duration * HOUR_MS : 0
    if (Number.isFinite(value)) this.lastConfirmed = { value, netGain: display.netGain, sourceAt: display.sourceAt, point: display.point }
  }

  snapshot(main: SessionExpCounter, now: number, running: boolean, activeDurationMs: number): {
    expReadout: ExpReadoutSnapshot; expRecovery?: ExpRecoveryMetric
  } {
    const incomplete = main.isIncomplete()
    const counter = incomplete ? this.recovery : main
    const display = counter.snapshot()
    const cached = incomplete ? (this.recovering ? this.lastConfirmed : null) : this.sessionConfirmed
    const fresh = cached !== null && now - cached.sourceAt <= this.holdMs
    const ready = cached !== null && display.status === 'ready' && (fresh || !running)
    const status = incomplete && !cached ? 'recovering' : ready ? 'ready' : fresh ? 'updating' : 'stale'
    const reason = incomplete ? counter.reviewReason() ?? this.gapReason ?? 'confirmation-timeout'
      : status === 'ready' ? null : main.reviewReason() ?? 'source-unreadable'
    if (status !== this.previousStatus) {
      if (this.previousStatus === 'ready' || status === 'ready') this.since = status === 'ready' ? null : now
      this.previousStatus = status
    }
    const duration = this.startedAt === null ? 0 : Math.max(0, activeDurationMs - this.baselineActiveAt)
    const confirmed = display.status === 'ready' && display.point !== null && display.netGain !== null
      && display.sourceAt !== null && display.sourceAt > this.boundary
      && (!incomplete || this.startedAt !== null)
      && (!running || now - display.sourceAt <= this.holdMs)
    return {
      expReadout: { status, scope: incomplete ? 'recovery' : 'session', incomplete, reason, since: this.since,
        confirmation: { status: confirmed ? 'ready' : 'pending', sourceAt: confirmed ? display.sourceAt : null },
        ...(incomplete ? { incompleteSince: main.incompleteSince(), incompleteReason: main.incompleteReason() } : {}),
        holdMs: this.holdMs, lastConfirmed: cached },
      ...(incomplete ? { expRecovery: { value: ready && display.netGain !== null && duration > 0 ? display.netGain / duration * HOUR_MS : null,
        status: ready ? 'ready' : 'unavailable', calculationStatus: display.status, updatedAt: cached?.sourceAt ?? null,
        startedAt: this.startedAt, activeDurationMs: duration, netGain: ready ? display.netGain : null,
        approximate: display.approximate } as ExpRecoveryMetric } : {}),
    }
  }
}
